import React from 'react'
import { cn } from 'utils/cn'

export interface ChipProps {
    variant?: 'primary' | 'secondary' | 'tertiary' | 'soft'
    type?: 'accent' | 'default' | 'success' | 'warning' | 'danger'
    size?: 'lg' | 'md' | 'sm'
    label?: React.ReactNode
    className?: string
    prefix?: React.ReactNode
    suffix?: React.ReactNode
    onClick?: () => void
    style?: React.CSSProperties
}

type ChipVariant = NonNullable<ChipProps['variant']>
type ChipType = NonNullable<ChipProps['type']>
type ChipSize = NonNullable<ChipProps['size']>

const sizeClasses: Record<ChipSize, string> = {
    lg: 'rounded-2xl px-2.5 py-1.5 text-sm',
    md: 'rounded-xl px-2 py-1 text-xs',
    sm: 'rounded-xl px-1.5 py-0.5 text-xs',
}

const colorClasses: Record<ChipVariant, Record<ChipType, string>> = {
    primary: {
        accent: 'bg-accent text-accent-foreground',
        default: 'bg-default text-default-foreground',
        success: 'bg-success text-success-foreground',
        warning: 'bg-warning text-warning-foreground',
        danger: 'bg-danger text-danger-foreground',
    },
    secondary: {
        accent: 'bg-default text-accent',
        default: 'bg-default text-default-foreground',
        success: 'bg-default text-success',
        warning: 'bg-default text-warning',
        danger: 'bg-default text-danger',
    },
    tertiary: {
        accent: 'bg-transparent text-accent',
        default: 'bg-transparent text-foreground',
        success: 'bg-transparent text-success',
        warning: 'bg-transparent text-warning',
        danger: 'bg-transparent text-danger',
    },
    soft: {
        accent: 'bg-accent-soft text-accent',
        default: 'bg-default text-default-foreground',
        success: 'bg-success-soft text-success',
        warning: 'bg-warning-soft text-warning',
        danger: 'bg-danger-soft text-danger',
    },
}

export const Chip = ({ variant = 'primary', type = 'default', size = 'md', label, prefix, suffix, onClick, style, className }: ChipProps) => {
    return (
        <span
            role={onClick ? 'button' : undefined}
            tabIndex={onClick ? 0 : undefined}
            onClick={onClick}
            onKeyDown={
                onClick
                    ? (e) => {
                          if (e.key === 'Enter' || e.key === ' ') onClick()
                      }
                    : undefined
            }
            className={cn(
                'box-border inline-flex items-center justify-center gap-0.5 whitespace-nowrap border-none font-medium',
                sizeClasses[size],
                colorClasses[variant][type],
                onClick ? 'cursor-pointer' : 'cursor-default',
                className
            )}
            style={style}
        >
            {prefix && <span className="flex shrink-0 items-center">{prefix}</span>}
            {label && <span>{label}</span>}
            {suffix && <span className="flex shrink-0 items-center">{suffix}</span>}
        </span>
    )
}
